// src/components/ExecutiveProtectionView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import {
  Shield, User, Mail, Phone, MapPin,
  AlertTriangle, Edit, Save, X,
  Linkedin, Twitter, ExternalLink, Plus,
  Eye, Lock, ChevronDown, ChevronUp, RefreshCw
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
const PROTECTION_STATUS_OPTS = ['Active', 'Monitoring', 'Inactive', 'Suspended'];
const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
const FINDING_STATUSES = ['Open', 'Investigating', 'Mitigated', 'Resolved', 'False Positive'];

const INP = "w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-lg text-slate-200 text-sm focus:outline-none focus:border-purple-500/50 transition-all";

// ─────────────────────────────────────────────────────────────────────────────
// Colour helpers (module-level so references are stable across renders)
// ─────────────────────────────────────────────────────────────────────────────
function getRiskColor(risk) {
  const m = {
    Critical: 'bg-red-500/20 text-red-400 border-red-500/30',
    High: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    Informational: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return m[risk] || m.Medium;
}

function getStatusColor(s) {
  if (s === 'Active') return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (s === 'Monitoring') return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
  if (s === 'Inactive') return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  if (s === 'Suspended') return 'bg-red-500/20 text-red-400 border-red-500/30';
  return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
}

function getFindingStatusColor(s) {
  if (s === 'Resolved') return 'bg-green-500/20 text-green-400 border-green-500/30';
  if (s === 'Open') return 'bg-red-500/20 text-red-400 border-red-500/30';
  if (s === 'Mitigated') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  if (s === 'False Positive') return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
}

function getFindingIcon(type) {
  if (type === 'Compromised Credentials') return Lock;
  if (type === 'Impersonating Account') return User;
  if (type === 'Suspicious Mention') return Eye;
  return AlertTriangle;
}

// ─────────────────────────────────────────────────────────────────────────────
// InfoRow — shared read/edit field  (module-level = stable)
// ─────────────────────────────────────────────────────────────────────────────
const InfoRow = ({ label, value, editing, editVal, onChange, type = 'text', isUrl = false }) => (
  <div>
    <p className="text-xs text-slate-400 mb-1">{label}</p>
    {editing ? (
      <input
        type={type}
        value={editVal ?? ''}
        onChange={e => onChange(e.target.value)}
        className={INP}
      />
    ) : (value != null && value !== '') ? (
      isUrl
        ? <a href={value} target="_blank" rel="noopener noreferrer"
            className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1 break-all">
            {value} <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        : <p className="text-sm text-slate-300 break-all">{String(value)}</p>
    ) : (
      <p className="text-sm text-slate-500 italic">—</p>
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// FindingCard — defined at MODULE level so React never remounts it on re-render
// ─────────────────────────────────────────────────────────────────────────────
const FindingCard = ({
  finding,
  isExpanded,
  onToggleExpand,
  isEditing,
  editData,
  onStartEdit,
  onCancelEdit,
  onFieldChange,
  onSave,
  saving,
  canEdit,
}) => {
  const Icon = getFindingIcon(finding.finding_type);
  const fe = editData;
  const setFe = (field, val) => onFieldChange(field, val);

  return (
    <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden hover:border-purple-500/30 transition-all">

      {/* Always-visible header */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-red-400" />
          </div>

          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input
                value={fe.title ?? ''}
                onChange={e => setFe('title', e.target.value)}
                className={INP + ' mb-2 font-semibold'}
              />
            ) : (
              <h4 className="text-base font-semibold text-slate-100 mb-1">{finding.title}</h4>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
              {finding.executive_name && (
                <span className="font-medium text-slate-300">
                  {finding.executive_name}{finding.executive_title ? ` — ${finding.executive_title}` : ''}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Icon className="w-3 h-3" />{finding.finding_type}
              </span>
              {finding.source && <span>Source: {finding.source}</span>}
              {finding.discovered_date && (
                <span>Discovered: {new Date(finding.discovered_date).toLocaleDateString()}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing ? (
              <select
                value={fe.severity ?? ''}
                onChange={e => setFe('severity', e.target.value)}
                className="px-2 py-1 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200 focus:outline-none"
              >
                {SEVERITIES.map(s => <option key={s}>{s}</option>)}
              </select>
            ) : (
              <span className={`px-2 py-1 rounded text-xs font-medium border ${getRiskColor(finding.severity)}`}>
                {finding.severity}
              </span>
            )}

            {canEdit && !isEditing && (
              <button onClick={onStartEdit} className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-all" title="Edit">
                <Edit className="w-4 h-4 text-slate-400" />
              </button>
            )}

            {isEditing && (
              <>
                <button onClick={onSave} disabled={saving}
                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 rounded-lg text-xs font-medium text-white transition-all flex items-center gap-1">
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  Save
                </button>
                <button onClick={onCancelEdit} className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-all">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </>
            )}

            <button onClick={onToggleExpand} className="p-1.5 hover:bg-slate-700/50 rounded-lg transition-all">
              {isExpanded
                ? <ChevronUp className="w-4 h-4 text-slate-400" />
                : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
          </div>
        </div>

        {/* Status badges */}
        <div className="flex flex-wrap gap-2 mt-3 pl-14">
          {isEditing ? (
            <select
              value={fe.status ?? ''}
              onChange={e => setFe('status', e.target.value)}
              className="px-2 py-1 bg-slate-700/50 border border-slate-600/50 rounded text-xs text-slate-200 focus:outline-none"
            >
              {FINDING_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          ) : (
            <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getFindingStatusColor(finding.status)}`}>
              {finding.status}
            </span>
          )}
          {finding.executive_notified && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">Executive Notified</span>
          )}
          {finding.law_enforcement_notified && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400">Law Enforcement Notified</span>
          )}
          {finding.reported_to_platform && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-cyan-500/20 text-cyan-400">Reported to Platform</span>
          )}
        </div>
      </div>

      {/* Expanded panel */}
      {isExpanded && (
        <div className="border-t border-slate-700/50 p-5 bg-slate-800/20 space-y-5">

          {finding.finding_type === 'Compromised Credentials' && (
            <div>
              <h5 className="text-xs font-semibold text-slate-400 uppercase mb-3 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Credential Details
              </h5>
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Compromised Email" value={finding.compromised_email}
                  editing={isEditing} editVal={fe.compromised_email} onChange={v => setFe('compromised_email', v)} />
                <InfoRow label="Data Breach" value={finding.data_breach_name}
                  editing={isEditing} editVal={fe.data_breach_name} onChange={v => setFe('data_breach_name', v)} />
                <InfoRow label="Credential Count" value={finding.credential_count}
                  editing={isEditing} editVal={fe.credential_count} type="number" onChange={v => setFe('credential_count', v)} />
                <div>
                  <p className="text-xs text-slate-400 mb-1">Password Exposed</p>
                  {isEditing ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!fe.password_exposed}
                        onChange={e => setFe('password_exposed', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-red-500" />
                      <span className="text-sm text-slate-300">Yes</span>
                    </label>
                  ) : (
                    <p className={`text-sm font-medium ${finding.password_exposed ? 'text-red-400' : 'text-slate-400'}`}>
                      {finding.password_exposed ? 'Yes — password/hash exposed' : 'No'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {finding.finding_type === 'Impersonating Account' && (
            <div>
              <h5 className="text-xs font-semibold text-slate-400 uppercase mb-3 flex items-center gap-1">
                <User className="w-3 h-3" /> Impersonating Account
              </h5>
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Handle" value={finding.impersonator_handle}
                  editing={isEditing} editVal={fe.impersonator_handle} onChange={v => setFe('impersonator_handle', v)} />
                <InfoRow label="Display Name" value={finding.impersonator_name}
                  editing={isEditing} editVal={fe.impersonator_name} onChange={v => setFe('impersonator_name', v)} />
                <InfoRow label="Profile URL" value={finding.impersonator_profile_url} isUrl
                  editing={isEditing} editVal={fe.impersonator_profile_url} onChange={v => setFe('impersonator_profile_url', v)} />
                <InfoRow label="Followers" value={finding.follower_count}
                  editing={isEditing} editVal={fe.follower_count} type="number" onChange={v => setFe('follower_count', v)} />
                <div>
                  <p className="text-xs text-slate-400 mb-1">Verified Badge</p>
                  {isEditing ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={!!fe.verified}
                        onChange={e => setFe('verified', e.target.checked)}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-red-500" />
                      <span className="text-sm text-slate-300">Has verified badge</span>
                    </label>
                  ) : (
                    <p className="text-sm text-slate-300">{finding.verified ? 'Yes' : 'No'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {finding.finding_type === 'Suspicious Mention' && (
            <div>
              <h5 className="text-xs font-semibold text-slate-400 uppercase mb-3 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Suspicious Mention
              </h5>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <InfoRow label="Threat Level" value={finding.threat_level}
                  editing={isEditing} editVal={fe.threat_level} onChange={v => setFe('threat_level', v)} />
                <InfoRow label="Mention Type" value={finding.mention_type}
                  editing={isEditing} editVal={fe.mention_type} onChange={v => setFe('mention_type', v)} />
                <InfoRow label="Mention URL" value={finding.mention_url} isUrl
                  editing={isEditing} editVal={fe.mention_url} onChange={v => setFe('mention_url', v)} />
              </div>
              {(finding.mention_context || isEditing) && (
                <div>
                  <p className="text-xs text-slate-400 mb-1">Context</p>
                  {isEditing
                    ? <textarea rows={3} value={fe.mention_context ?? ''} onChange={e => setFe('mention_context', e.target.value)} className={INP} />
                    : <p className="text-sm text-slate-300 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 whitespace-pre-wrap">{finding.mention_context}</p>
                  }
                </div>
              )}
            </div>
          )}

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-3">
            {(finding.platform || isEditing) && (
              <InfoRow label="Platform" value={finding.platform}
                editing={isEditing} editVal={fe.platform} onChange={v => setFe('platform', v)} />
            )}
            {(finding.url || isEditing) && (
              <InfoRow label="URL" value={finding.url} isUrl
                editing={isEditing} editVal={fe.url} onChange={v => setFe('url', v)} />
            )}
            {(finding.screenshot_url || isEditing) && (
              <InfoRow label="Screenshot URL" value={finding.screenshot_url} isUrl
                editing={isEditing} editVal={fe.screenshot_url} onChange={v => setFe('screenshot_url', v)} />
            )}
          </div>

          {(finding.details || isEditing) && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Details</p>
              {isEditing
                ? <textarea rows={3} value={fe.details ?? ''} onChange={e => setFe('details', e.target.value)} className={INP} />
                : <p className="text-sm text-slate-300 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 whitespace-pre-wrap">{finding.details}</p>
              }
            </div>
          )}

          {(finding.recommendations || isEditing) && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Recommendations</p>
              {isEditing
                ? <textarea rows={3} value={fe.recommendations ?? ''} onChange={e => setFe('recommendations', e.target.value)} className={INP} />
                : <p className="text-sm text-slate-300 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 whitespace-pre-wrap">{finding.recommendations}</p>
              }
            </div>
          )}

          {(finding.notes || isEditing) && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Internal Notes</p>
              {isEditing
                ? <textarea rows={2} value={fe.notes ?? ''} onChange={e => setFe('notes', e.target.value)} className={INP} />
                : <p className="text-sm text-slate-400 bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 italic whitespace-pre-wrap">{finding.notes}</p>
              }
            </div>
          )}

          {isEditing && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Notification Flags</p>
              <div className="flex flex-wrap gap-4">
                {[
                  ['executive_notified', 'Executive Notified'],
                  ['law_enforcement_notified', 'Law Enforcement Notified'],
                  ['reported_to_platform', 'Reported to Platform'],
                ].map(([field, label]) => (
                  <label key={field} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!fe[field]}
                      onChange={e => setFe(field, e.target.checked)}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-purple-500" />
                    <span className="text-sm text-slate-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ExecDetailModal — defined at MODULE level (stable identity)
// ─────────────────────────────────────────────────────────────────────────────
const ExecDetailModal = ({
  exec, findings, editing, editData,
  onStartEdit, onCancelEdit, onFieldChange, onSave, saving,
  onClose, canEdit,
}) => {
  if (!exec) return null;

  const d = editing ? editData : exec;
  const setE = (field, val) => onFieldChange(field, val);
  const related = findings.filter(f => f.executive_id === exec.executive_id);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30 flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold text-slate-100 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-400" /> Executive Details
          </h3>
          <div className="flex items-center gap-2">
            {canEdit && !editing && (
              <button onClick={onStartEdit}
                className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-xs font-medium text-purple-300 transition-all flex items-center gap-1">
                <Edit className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            {editing && (
              <>
                <button onClick={onSave} disabled={saving}
                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 rounded-lg text-xs font-medium text-white transition-all flex items-center gap-1">
                  {saving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save Changes
                </button>
                <button onClick={onCancelEdit}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-medium text-slate-300 transition-all">
                  Cancel
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 hover:bg-slate-800/50 rounded-lg transition-all">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Avatar + name row */}
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0">
              {d.photo_url
                ? <img src={d.photo_url} alt={d.name} className="w-full h-full rounded-full object-cover" />
                : <span className="text-2xl font-bold text-white">{d.name?.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
              }
            </div>
            <div className="flex-1 space-y-2">
              {editing ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-400 mb-1 block">Full Name</label>
                    <input value={d.name ?? ''} onChange={e => setE('name', e.target.value)} className={INP} /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Title</label>
                    <input value={d.title ?? ''} onChange={e => setE('title', e.target.value)} className={INP} /></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Department</label>
                    <input value={d.department ?? ''} onChange={e => setE('department', e.target.value)} className={INP} /></div>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-100">{d.name}</h2>
                  <p className="text-lg text-slate-300">{d.title}</p>
                  {d.department && <p className="text-sm text-slate-400">{d.department}</p>}
                </>
              )}
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              {editing ? (
                <>
                  <div><label className="text-xs text-slate-400 mb-1 block">Risk Level</label>
                    <select value={d.risk_level ?? 'Medium'} onChange={e => setE('risk_level', e.target.value)} className={INP}>
                      {RISK_LEVELS.map(r => <option key={r}>{r}</option>)}
                    </select></div>
                  <div><label className="text-xs text-slate-400 mb-1 block">Protection Status</label>
                    <select value={d.protection_status ?? 'Active'} onChange={e => setE('protection_status', e.target.value)} className={INP}>
                      {PROTECTION_STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
                    </select></div>
                </>
              ) : (
                <>
                  <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getRiskColor(d.risk_level)}`}>
                    {d.risk_level} Risk
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium border ${getStatusColor(d.protection_status)}`}>
                    {d.protection_status}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Contact */}
          <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase mb-3">Contact Information</h4>
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-slate-400 mb-1 block">Primary Email</label>
                  <input type="email" value={d.primary_email ?? ''} onChange={e => setE('primary_email', e.target.value)} className={INP} /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Phone</label>
                  <input type="tel" value={d.phone ?? ''} onChange={e => setE('phone', e.target.value)} className={INP} /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Location</label>
                  <input value={d.location ?? ''} onChange={e => setE('location', e.target.value)} className={INP} /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Photo URL</label>
                  <input type="url" value={d.photo_url ?? ''} onChange={e => setE('photo_url', e.target.value)} className={INP} /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">LinkedIn URL</label>
                  <input type="url" value={d.linkedin_url ?? ''} onChange={e => setE('linkedin_url', e.target.value)} className={INP} /></div>
                <div><label className="text-xs text-slate-400 mb-1 block">Twitter Handle</label>
                  <input value={d.twitter_handle ?? ''} onChange={e => setE('twitter_handle', e.target.value)} className={INP} /></div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                {d.primary_email && <div className="flex items-center gap-2 text-slate-300"><Mail className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>{d.primary_email}</span></div>}
                {d.phone && <div className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>{d.phone}</span></div>}
                {d.location && <div className="flex items-center gap-2 text-slate-300"><MapPin className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>{d.location}</span></div>}
                {d.linkedin_url && (
                  <div className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <a href={d.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                      LinkedIn <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {d.twitter_handle && <div className="flex items-center gap-2 text-slate-300"><Twitter className="w-4 h-4 text-slate-500 flex-shrink-0" /><span>{d.twitter_handle}</span></div>}
              </div>
            )}

            {/* Additional emails */}
            {(d.additional_emails?.length > 0 || editing) && (
              <div className="mt-4 pt-4 border-t border-slate-700/50">
                <h5 className="text-xs font-semibold text-slate-400 uppercase mb-2 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Additional Emails
                </h5>
                {editing ? (
                  <div className="space-y-2">
                    {(d.additional_emails || []).map((email, i) => (
                      <div key={i} className="flex gap-2">
                        <input type="email" value={email}
                          onChange={e => {
                            const arr = [...(d.additional_emails || [])];
                            arr[i] = e.target.value;
                            setE('additional_emails', arr);
                          }}
                          className={INP} />
                        <button onClick={() => setE('additional_emails', (d.additional_emails || []).filter((_, idx) => idx !== i))}
                          className="p-2 hover:bg-red-500/10 rounded-lg text-red-400 flex-shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button onClick={() => setE('additional_emails', [...(d.additional_emails || []), ''])}
                      className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300">
                      <Plus className="w-3 h-3" /> Add email
                    </button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {d.additional_emails.map((email, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <Mail className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" /><span>{email}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase mb-2">Notes</h4>
            {editing
              ? <textarea rows={3} value={d.notes ?? ''} onChange={e => setE('notes', e.target.value)} className={INP} placeholder="Additional notes..." />
              : d.notes
                ? <p className="text-sm text-slate-200 whitespace-pre-wrap">{d.notes}</p>
                : <p className="text-sm text-slate-500 italic">No notes.</p>
            }
          </div>

          {/* Related findings */}
          {related.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-300 mb-3">Related Findings ({related.length})</h4>
              <div className="space-y-2">
                {related.map(f => (
                  <div key={f.finding_id} className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-200">{f.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{f.finding_type}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getRiskColor(f.severity)}`}>{f.severity}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const ExecutiveProtectionView = ({
  canSeeSuperAdminSettings,
  canEditIncidents,
  setShowAddExecutive,
  setShowAddExecutiveFinding,
  findingRefreshKey,
}) => {
  const { getToken, isSignedIn } = useAuth();
  const { activeCompany } = useTenant();

  const [executives, setExecutives] = useState([]);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [activeTab, setActiveTab] = useState('executives');

  const [editingExec, setEditingExec] = useState(false);
  const [execEditData, setExecEditData] = useState({});
  const [savingExec, setSavingExec] = useState(false);

  const [editingFindingId, setEditingFindingId] = useState(null);
  const [findingEditData, setFindingEditData] = useState({});
  const [savingFinding, setSavingFinding] = useState(false);

  const [expandedFindings, setExpandedFindings] = useState({});

  useEffect(() => {
    if (!isSignedIn || !activeCompany) return;
    setLoading(true);
    setError(null);
    getToken()
      .then(token => fetch(`/api/companies/${activeCompany.id}/executives`, { headers: { Authorization: `Bearer ${token}` } }))
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then(data => setExecutives(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [isSignedIn, activeCompany, getToken]);

  useEffect(() => {
    if (!isSignedIn || !activeCompany) return;
    getToken()
      .then(token => fetch(`/api/companies/${activeCompany.id}/executive-findings`, { headers: { Authorization: `Bearer ${token}` } }))
      .then(res => res.ok ? res.json() : [])
      .then(data => setFindings(Array.isArray(data) ? data : []))
      .catch(e => console.error('Findings error:', e));
  }, [isSignedIn, activeCompany, getToken, findingRefreshKey]);

  const handleSaveExec = async () => {
    setSavingExec(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/companies/${activeCompany.id}/executives/${selectedExecutive.executive_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(execEditData),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setExecutives(prev => prev.map(e => e.executive_id === updated.executive_id ? updated : e));
      setSelectedExecutive(updated);
      setEditingExec(false);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSavingExec(false); }
  };

  const handleSaveFinding = async (findingId) => {
    setSavingFinding(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/companies/${activeCompany.id}/executive-findings/${findingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(findingEditData),
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setFindings(prev => prev.map(f => f.finding_id === updated.finding_id ? updated : f));
      setEditingFindingId(null);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSavingFinding(false); }
  };

  // useCallback so FindingCard's onFieldChange reference is stable
  const handleFindingFieldChange = useCallback((field, val) => {
    setFindingEditData(prev => ({ ...prev, [field]: val }));
  }, []);

  const handleExecFieldChange = useCallback((field, val) => {
    setExecEditData(prev => ({ ...prev, [field]: val }));
  }, []);

  const getFindingsCount = id => findings.filter(f => f.executive_id === id).length;
  const getCriticalCount = id => findings.filter(f => f.executive_id === id && f.severity === 'Critical').length;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-8 py-6 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
              <Shield className="w-7 h-7 text-purple-400" /> Executive Protection
            </h2>
            <p className="text-sm text-slate-400 mt-1">Monitor and protect high-value individuals</p>
          </div>
          {canSeeSuperAdminSettings && (
            <div className="flex gap-2">
              <button onClick={() => setShowAddExecutive(true)}
                className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm text-white font-medium transition-all flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Executive
              </button>
              <button onClick={() => setShowAddExecutiveFinding(true)}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-sm text-white font-medium transition-all flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Report Finding
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-4">
          {['executives', 'findings'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${activeTab === tab ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'text-slate-400 hover:bg-slate-800/50'}`}>
              {tab === 'executives'
                ? <><User className="w-4 h-4 inline-block mr-2" />Executives ({executives.length})</>
                : <><AlertTriangle className="w-4 h-4 inline-block mr-2" />Findings ({findings.length})</>
              }
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-8">

        {/* EXECUTIVES TAB */}
        {activeTab === 'executives' && (
          <div>
            {loading && <p className="text-slate-400">Loading executives...</p>}
            {error && <div className="bg-red-500/10 border border-red-500/30 rounded p-4 text-sm text-red-300">{error}</div>}
            {!loading && executives.length === 0 && (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No executives registered for protection</p>
                {canSeeSuperAdminSettings && (
                  <button onClick={() => setShowAddExecutive(true)}
                    className="px-4 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-sm text-white transition-all">
                    Add Your First Executive
                  </button>
                )}
              </div>
            )}
            {!loading && executives.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {executives.map(exec => {
                  const findingsCount = getFindingsCount(exec.executive_id);
                  const criticalCount = getCriticalCount(exec.executive_id);
                  return (
                    <div key={exec.executive_id}
                      className="bg-slate-800/30 border border-slate-700/50 rounded-xl overflow-hidden hover:border-purple-500/50 transition-all cursor-pointer"
                      onClick={() => setSelectedExecutive(exec)}>

                      {/* Top — avatar + name */}
                      <div className="p-5 bg-gradient-to-r from-purple-900/20 to-slate-800/20 border-b border-slate-700/50">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center flex-shrink-0">
                            {exec.photo_url
                              ? <img src={exec.photo_url} alt={exec.name} className="w-full h-full rounded-full object-cover" />
                              : <span className="text-lg font-bold text-white">{exec.name?.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-semibold text-slate-100 truncate">{exec.name}</h3>
                            {exec.title && <p className="text-sm text-slate-400 truncate">{exec.title}</p>}
                            {exec.department && <p className="text-xs text-slate-500 mt-0.5 truncate">{exec.department}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Middle — contact details */}
                      <div className="px-5 py-4 space-y-2 text-sm border-b border-slate-700/50">
                        {exec.primary_email && (
                          <div className="flex items-center gap-2 text-slate-300 min-w-0">
                            <Mail className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <span className="truncate">{exec.primary_email}</span>
                          </div>
                        )}
                        {exec.additional_emails?.map((em, i) => (
                          <div key={i} className="flex items-center gap-2 text-slate-400 min-w-0">
                            <Mail className="w-3.5 h-3.5 text-slate-600 flex-shrink-0" />
                            <span className="truncate text-xs">{em}</span>
                          </div>
                        ))}
                        {exec.phone && (
                          <div className="flex items-center gap-2 text-slate-300">
                            <Phone className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <span>{exec.phone}</span>
                          </div>
                        )}
                        {exec.location && (
                          <div className="flex items-center gap-2 text-slate-300">
                            <MapPin className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <span className="truncate">{exec.location}</span>
                          </div>
                        )}
                        {exec.linkedin_url && (
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <Linkedin className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <a href={exec.linkedin_url} target="_blank" rel="noopener noreferrer"
                              className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                              LinkedIn <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          </div>
                        )}
                        {exec.twitter_handle && (
                          <div className="flex items-center gap-2 text-slate-300">
                            <Twitter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <span>{exec.twitter_handle}</span>
                          </div>
                        )}
                      </div>

                      {/* Bottom — badges + findings */}
                      <div className="px-5 py-4 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getRiskColor(exec.risk_level)}`}>
                            {exec.risk_level} Risk
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium border ${getStatusColor(exec.protection_status)}`}>
                            {exec.protection_status}
                          </span>
                        </div>
                        {findingsCount > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">Findings:</span>
                            <div className="flex items-center gap-2">
                              {criticalCount > 0 && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-medium">{criticalCount} Critical</span>
                              )}
                              <span className="text-slate-300 font-medium">{findingsCount} total</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* FINDINGS TAB */}
        {activeTab === 'findings' && (
          <div>
            {findings.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No findings reported yet</p>
                {canSeeSuperAdminSettings && (
                  <button onClick={() => setShowAddExecutiveFinding(true)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-sm text-white transition-all">
                    Report Your First Finding
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {findings.map(finding => (
                  <FindingCard
                    key={finding.finding_id}
                    finding={finding}
                    isExpanded={!!expandedFindings[finding.finding_id]}
                    onToggleExpand={() => setExpandedFindings(prev => ({ ...prev, [finding.finding_id]: !prev[finding.finding_id] }))}
                    isEditing={editingFindingId === finding.finding_id}
                    editData={editingFindingId === finding.finding_id ? findingEditData : finding}
                    onStartEdit={() => { setFindingEditData({ ...finding }); setEditingFindingId(finding.finding_id); }}
                    onCancelEdit={() => { setEditingFindingId(null); setFindingEditData({}); }}
                    onFieldChange={handleFindingFieldChange}
                    onSave={() => handleSaveFinding(finding.finding_id)}
                    saving={savingFinding}
                    canEdit={canSeeSuperAdminSettings}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Executive Detail Modal */}
      <ExecDetailModal
        exec={selectedExecutive}
        findings={findings}
        editing={editingExec}
        editData={execEditData}
        onStartEdit={() => { setExecEditData({ ...selectedExecutive }); setEditingExec(true); }}
        onCancelEdit={() => { setEditingExec(false); setExecEditData({}); }}
        onFieldChange={handleExecFieldChange}
        onSave={handleSaveExec}
        saving={savingExec}
        onClose={() => { setSelectedExecutive(null); setEditingExec(false); setExecEditData({}); }}
        canEdit={canSeeSuperAdminSettings}
      />
    </div>
  );
};

export default ExecutiveProtectionView;